import { getAnalyticsIdentityForAuth, completeAnalyticsAuthentication } from '../services/analyticsService';
// src/components/LoginModal.js
import React, { useEffect, useState } from 'react';
import { SITE_CONFIG } from '../config';
import './LoginModal.css';

const ADMIN_ROLES = ['ROLE_ADMIN', 'ROLE_ASSISTANT', 'ROLE_LEAD'];

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData({ username: '', password: '' });
      setMessage('');
      setError('');
      setSubmitting(false);
      setResetMode(false);
      setResetEmail('');
    }
  }, [isOpen]);

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail.trim(),
          baseUrl: window.location.origin,
          siteId: SITE_CONFIG.SITE_ID
        })
      });
      if (response.ok) {
        setMessage('Reset link requested. Check your email for instructions.');
      } else {
        setError('Could not request a reset link. Please try again.');
      }
    } catch (err) {
      setError('We could not connect to the reset service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (nextResetMode) => {
    setResetMode(nextResetMode);
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          analyticsIdentity: await getAnalyticsIdentityForAuth(),
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      const responseBody = await response.text();
      let data = {};
      try {
        data = responseBody ? JSON.parse(responseBody) : {};
      } catch (parseError) {
        data = {};
      }

      if (response.ok) {
        const roles = Array.isArray(data.roles) ? data.roles : [];
        const isAuthorized = roles.some(role => ADMIN_ROLES.includes(role));
        const matchesSite = !data.siteId || data.siteId.toLowerCase() === SITE_CONFIG.SITE_ID.toLowerCase();

        if (!isAuthorized || !matchesSite) {
          setError('This account does not have access to the Maricopa Renovations admin console.');
          return;
        }

        localStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('user_roles', JSON.stringify(roles));
        const token = data.token || data.accessToken;
        if (token) localStorage.setItem('jwt_token', token);

        completeAnalyticsAuthentication(data, 'login');
        setMessage('Login successful. Opening the admin console…');
        onLogin?.(data);
      } else {
        setError(data.message || 'Login failed. Please check your username and password.');
      }
    } catch (err) {
      setError('We could not connect to the login service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal-content" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="login-title">
        <button className="modal-close" type="button" onClick={onClose} aria-label="Close login">&times;</button>
        <form onSubmit={resetMode ? handleResetSubmit : handleSubmit} className="login-form">
          <h2 id="login-title">{resetMode ? 'Reset your password' : 'Owner login'}</h2>
          
          {message && <div className="success-message" role="status">{message}</div>}
          {error && <div className="error-message" role="alert">{error}</div>}

          {resetMode ? <>
            <p>Enter your account email to receive a password reset link.</p>
            <div className="form-group">
              <label htmlFor="reset-email">Email:</label>
              <input id="reset-email" type="email" autoComplete="email" value={resetEmail}
                onChange={event => setResetEmail(event.target.value)} autoFocus required />
            </div>
          </> : <>
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              autoComplete="username"
              autoFocus
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />
          </div>

          </>}

          <button type="submit" className="submit-button" disabled={submitting}>
            {resetMode ? (submitting ? 'Sending…' : 'Send reset link') : (submitting ? 'Signing in…' : 'Login')}
          </button>
          <button type="button" className="login-reset-link" disabled={submitting} onClick={() => switchMode(!resetMode)}>
            {resetMode ? 'Back to owner login' : 'Forgot password?'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
